import {
  Dropdown,
  IconButton,
  MessageBar,
  MessageBarType,
  Stack
} from "@fluentui/react";
import { useState } from "react";

import { ITEMS_PER_PAGE_OPTIONS } from "../../lib/constants";

import { DatePicker } from "@/core/libs/list-data/lib/filters/DatePicker";
import { DateRangerPickerFilter } from "@/core/libs/list-data/lib/filters/DateRangerPicker";
import { ListDataFiltersProps } from "@/core/libs/list-data/lib/filters/interfaces";
import { MaskedText } from "@/core/libs/list-data/lib/filters/MaskedTextFilter";
import { SelectFilter } from "@/core/libs/list-data/lib/filters/SelectFilter";
import { TextArrayFilter } from "@/core/libs/list-data/lib/filters/TextArrayFilter";
import { TextFilter } from "@/core/libs/list-data/lib/filters/TextFilter";
import { ListDataFiltersTypeEnum } from "@/core/libs/list-data/lib/interfaces/enums";
import { FiltersContainer } from "@/core/libs/list-data/lib/styles";

export function ListDataFilters({
  _paginate,
  setVisibleTerms,
  setSelectedKeys,
  setSelectedTerms,
  toggleFilterVisibility,
  filters,
  visibleTerms,
  selectedKeys,
  itemsPerPage,
  selectedTerms,
  debouncedTerms,
  isItemsPerPageFilterDisabled,
  isCloseFilterButtonDisabled,
  filterTitle,
  massActionsType
}: ListDataFiltersProps): JSX.Element {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  return (
    <FiltersContainer
      style={{
        padding: "8px",
        paddingBottom: "20px",
        background: massActionsType ? "none" : "#faf9f8",
        borderBottom: massActionsType
          ? "1px solid rgba(122, 148, 200)"
          : "1px solid #edebe9",
        borderTop: massActionsType
          ? "1px solid rgb(122, 148, 200)"
          : "1px solid #edebe9"
      }}
      onClick={() => {
        Boolean(errorMessage) && setErrorMessage(null);
        Boolean(successMessage) && setSuccessMessage(null);
      }}
    >
      {filterTitle && (
        <h3
          style={{
            margin: "auto",
            marginBottom: "10px",
            fontSize: "16px",
            fontWeight: 600
          }}
        >
          {filterTitle}
        </h3>
      )}
      <Stack
        horizontal
        horizontalAlign="space-between"
        onClick={() => {
          setErrorMessage(null);
        }}
      >
        <Stack tokens={{ childrenGap: 10 }} horizontal wrap>
          {filters?.length > 0 &&
            filters
              .filter(f => !f.massActionsType)
              .map((filter, index) => {
                switch (filter.type) {
                  case ListDataFiltersTypeEnum.SELECT: {
                    return (
                      <SelectFilter
                        key={`${Array.isArray(filter.filterQuery) ? filter.filterQuery.join(",") : filter.filterQuery}${index}`}
                        filterConfigs={{ ...filter, maxWidth: "300px" }}
                        debouncedTerms={debouncedTerms}
                        selectedKeys={selectedKeys}
                        itemsPerPage={itemsPerPage}
                        _paginate={_paginate}
                        setSelectedKeys={setSelectedKeys}
                      />
                    );
                  }

                  case ListDataFiltersTypeEnum.DATE: {
                    return (
                      <DatePicker
                        key={`${Array.isArray(filter.filterQuery) ? filter.filterQuery.join(",") : filter.filterQuery}${index}`}
                        filterConfigs={filter}
                        debouncedTerms={debouncedTerms}
                        selectedKeys={selectedKeys}
                        itemsPerPage={itemsPerPage}
                        _paginate={_paginate}
                        setSelectedKeys={setSelectedKeys}
                      />
                    );
                  }

                  case ListDataFiltersTypeEnum.DATE_RANGE: {
                    return (
                      <DateRangerPickerFilter
                        key={`${Array.isArray(filter.filterQuery) ? filter.filterQuery.join(",") : filter.filterQuery}${index}`}
                        filterConfigs={filter}
                        debouncedTerms={debouncedTerms}
                        selectedKeys={selectedKeys}
                        itemsPerPage={itemsPerPage}
                        _paginate={_paginate}
                        setSelectedKeys={setSelectedKeys}
                      />
                    );
                  }

                  case ListDataFiltersTypeEnum.MASK_TEXT: {
                    return (
                      <MaskedText
                        key={`${Array.isArray(filter.filterQuery) ? filter.filterQuery.join(",") : filter.filterQuery}${index}`}
                        filterConfigs={filter}
                        selectedTerms={selectedTerms}
                        visibleTerms={visibleTerms}
                        setVisibleTerms={setVisibleTerms}
                        setSelectedTerms={setSelectedTerms}
                        mask={filter.mask}
                      />
                    );
                  }

                  case ListDataFiltersTypeEnum.ARRAY_TEXT: {
                    return (
                      <TextArrayFilter
                        key={`${Array.isArray(filter.filterQuery) ? filter.filterQuery.join(",") : filter.filterQuery}${index}`}
                        filterConfigs={filter}
                        selectedTerms={selectedTerms}
                        visibleTerms={visibleTerms}
                        setVisibleTerms={setVisibleTerms}
                        setSelectedTerms={setSelectedTerms}
                        sendArrayAsCommaSeparatedString={
                          filter.sendArrayAsCommaSeparatedString
                        }
                      />
                    );
                  }

                  default: {
                    return (
                      <TextFilter
                        key={`${Array.isArray(filter.filterQuery) ? filter.filterQuery.join(",") : filter.filterQuery}${index}`}
                        filterConfigs={filter}
                        selectedTerms={selectedTerms}
                        visibleTerms={visibleTerms}
                        setVisibleTerms={setVisibleTerms}
                        setSelectedTerms={setSelectedTerms}
                      />
                    );
                  }
                }
              })}

          {!isItemsPerPageFilterDisabled && (
            <Stack
              tokens={{ childrenGap: 5 }}
              verticalAlign="start"
              styles={{ root: { width: "100%" } }}
            >
              <Dropdown
                placeholder="Quantidade por página"
                label="Quantidade por página"
                selectedKey={String(itemsPerPage)}
                options={ITEMS_PER_PAGE_OPTIONS}
                onChange={(_, value) => {
                  _paginate({
                    limit: Number(value.key),
                    ...selectedKeys,
                    ...selectedTerms
                  });
                }}
                style={{ width: "12rem" }}
              />
            </Stack>
          )}
        </Stack>

        {!isCloseFilterButtonDisabled && (
          <IconButton
            iconProps={{ iconName: "ChromeClose" }}
            title="Fechar"
            ariaLabel="Fechar"
            onClick={toggleFilterVisibility}
          />
        )}
      </Stack>

      {Boolean(errorMessage) && (
        <MessageBar messageBarType={MessageBarType.error} isMultiline>
          {errorMessage}
        </MessageBar>
      )}
      {Boolean(successMessage) && (
        <MessageBar messageBarType={MessageBarType.success} isMultiline>
          {successMessage}
        </MessageBar>
      )}
    </FiltersContainer>
  );
}
